import React, { useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "reactstrap"
import EvaluationSection1 from "./EvaluationSection1"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import AcWaEvaluationSection from "./AcWaEvaluationSection"
import CustomLabel from "components/Common/CustomLabel"

const EvaluationModal = ({ isOpen, toggle, data }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [comment, setComment] = useState("")

  const isMark = useMemo(() => {
    if (!data || data.length === 0) return false

    return data.some(section => {
      return section?.main_question?.question_type === "mark"
    })
  }, [data])

  const [responses, setResponses] = useState({})
  const [acwaAnswers, setAcwaAnswers] = useState({})

  const handleAcwaSectionChange = (sectionId, answers) => {
    setAcwaAnswers(prev => ({
      ...prev,
      [sectionId]: { ...answers, sectionId },
    }))
  }

  const handleAnswerChange = (sectionId, index, questionId, answer) => {
    setResponses(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [index]: { id: questionId, answer },
      },
    }))
  }

  const handleSubmit = () => {
    const result = {}
    let allValid = true

    for (const section of data) {
      const sectionAnswers = responses[section.main_question.id]
      if (
        !sectionAnswers ||
        Object.keys(sectionAnswers).length !== section.sub_questions.length
      ) {
        allValid = false
        break
      }

      const hasNo = Object.values(sectionAnswers).some(q => q.answer === false)
      result[section.main_question.id] = {
        questions: sectionAnswers,
        overallAnswer: hasNo ? false : true,
      }
    }

    if (!allValid) {
      return dispatch(
        showAlert({ text: t("Please answer all questions"), type: "error" })
      )
    }

    const questions = []

    for (const [sectionId, sectionData] of Object.entries(result)) {
      // Add sub-question answers
      for (const q of Object.values(sectionData.questions)) {
        questions.push({ id: q.id, answer: q.answer })
      }

      // Add main question answer using sectionId as id
      questions.push({
        id: parseInt(sectionId),
        answer: sectionData.overallAnswer,
      })
    }

    toggle({ answers: questions, comment })
  }

  const handleSubmitAc = () => {
    const payload = {
      evaluation_type: "ac_wa",
      answers: [],
    }

    let hasError = false

    // Assuming `acwaSections` is the API data you rendered from
    data.forEach(section => {
      const savedSection = acwaAnswers[section.id]

      // If user hasn't answered this section at all
      if (!savedSection || !savedSection.questions) {
        hasError = true
        return
      }

      // Check if all sub-questions from API are answered
      const allAnswered = section.sub_questions.every(
        q => savedSection.questions[q.id] !== undefined
      )

      if (!allAnswered) {
        hasError = true
        return
      }

      Object.entries(savedSection.questions).forEach(([questionId, mark]) => {
        payload.answers.push({
          id: parseInt(questionId),
          mark,
        })
      })

      payload.answers.push({
        id: savedSection.main_question_id,
        mark: savedSection.total_mark,
      })
    })

    // if (!hasError) {
    //   return dispatch(
    //     showAlert({ text: t("Please answer all questions"), type: "error" })
    //   )
    // }

    toggle({ answers: payload.answers, comment })
  }

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()} size="lg">
      <ModalHeader toggle={() => toggle()}>{t("Evaluation")}</ModalHeader>
      <ModalBody>
        {!isMark
          ? data.map(section => (
              <EvaluationSection1
                key={section.id}
                section={section}
                onAnswerChange={handleAnswerChange}
                answers={responses[section.id]}
              />
            ))
          : data.map(section => (
              <AcWaEvaluationSection
                key={section.id}
                section={section}
                onAnswerChange={handleAcwaSectionChange}
                answers={acwaAnswers[section.id]}
              />
            ))}

        <div className="my-2">
          <CustomLabel label={t("Comment")} htmlFor={"comment"} />
          <Input
            value={comment}
            onChange={e => setComment(e.target.value)}
            type="textarea"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => toggle()}>
          {t("Close")}
        </Button>
        <Button
          onClick={isMark ? handleSubmitAc : handleSubmit}
          color="primary"
        >
          {t("Submit")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default EvaluationModal
